import RewardAPI from "@api/Reward.api";
import UserAPI from "@api/User.api";
import type { IReward } from "@api/models/IReward";
import ButtonVariants from "@core-components/button/ButtonVariants";
import SSHRef from "@core-components/links/HRef";
import { EPrivateRoutes } from "@core-types/routes.enum";
import useAppLanguage from "@hooks/language/useAppLanguage";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Flex, SimpleGrid, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import useRewardModalStore from "@store/rewardModal.store";
import useUserStore from "@store/user.store";
import { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import ClientDashboardTitle from "./components/ClientDashboardTitle";
import CoinCard from "./components/CoinCard";
import Coins, { ECoinVariant } from "./components/Coins";
import NextUserRewardCard from "./components/NextUserRewardCard";
import RewardCard from "./components/RewardCard";
import RewardHistoryModal from "./components/RewardHistoryModal";
import ContainerCard from "./components/container/ContainerCard";
import ContainerText from "./components/container/ContainerText";

const Rewards: React.FC = () => {
  const { setAffiliationInformation } = useUserStore();
  const { open } = useRewardModalStore();
  const { t } = useAppTranslation("pages.clientDashboard");
  const { locationUrlBuilder } = useAppLanguage();

  const [showRewardHistory, rewardHistory] = useDisclosure(false);
  const [rewards, setRewards] = useState<IReward[]>([]);

  const handleBuyReward = (rewardId: number) => {
    RewardAPI.orderReward(rewardId).then(async (item) => {
      UserAPI.getMeInformation().then((res) => setAffiliationInformation(res));

      open(item);
    });
  };

  useEffect(() => {
    RewardAPI.getRewardList().then(setRewards);
  }, []);

  return (
    <>
      <Stack gap={20} classNames={{ root: "max-w-[1400px]" }}>
        <ClientDashboardTitle
          icon={<Coins variant={ECoinVariant.REWARDS} size={40} />}
          label={t("rewards.title")}
          subHeader={t("rewards.subHeader")}
        />
        <Flex className="flex-col items-stretch gap-[20px] sm:flex-row">
          <CoinCard
            displayButton={false}
            containerProps={{
              fullWidth: true,
              classNames: {
                root: "flex-[0.5]",
                wrapper: "w-full",
              },
            }}
          />

          <ContainerCard
            title={t("rewards.howToEarn.title")}
            classNames={{ root: "flex-1" }}
          >
            <ContainerText>
              <Trans i18nKey="pages.clientDashboard.rewards.howToEarn.text">
                Gagnez des pièces en effectuant des achats grâces à notre
                programme de&nbsp;
                <SSHRef
                  openInNewTab={false}
                  href={locationUrlBuilder(EPrivateRoutes.FIDELITY)}
                >
                  fidélité
                </SSHRef>
                &nbsp; et en&nbsp;
                <SSHRef
                  openInNewTab={false}
                  href={locationUrlBuilder(EPrivateRoutes.AFFILIATION)}
                >
                  parrainant
                </SSHRef>
                &nbsp; vos amis.
              </Trans>
            </ContainerText>
          </ContainerCard>
        </Flex>
        <Flex className="max-w-[1200px] flex-col items-stretch gap-[20px] sm:flex-row">
          <NextUserRewardCard
            containerProps={{ classNames: { root: "w-full sm:flex-1" } }}
          />

          {/* TODO : put into a component file */}
          <ContainerCard
            title={t("rewards.history.title")}
            classNames={{
              root: "w-full sm:flex-1",
              wrapper: "w-auto xs:w-full flex h-full items-center",
            }}
          >
            <ButtonVariants.Gradient
              size="lg"
              isFullWidth
              className="gradient-blue-light-purple rounded-lg"
              classNames={{
                label: "text-pretty whitespace-normal",
              }}
              label={t("rewards.history.button")}
              onClick={rewardHistory.open}
            />
          </ContainerCard>
        </Flex>
        <Stack>
          <Text className="text-center font-bold font-inter text-primary-white">
            {t("rewards.onlyEUWAccounts")}
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 2, xs: 2, lg: 3 }}>
            {rewards.map((reward) => (
              <RewardCard
                key={reward.i18nKey}
                reward={reward}
                onBuy={handleBuyReward}
              />
            ))}
          </SimpleGrid>
        </Stack>
      </Stack>
      <RewardHistoryModal
        opened={showRewardHistory}
        onClose={rewardHistory.close}
      />
    </>
  );
};

export default Rewards;
